/**
 * Real User Monitoring (RUM) & Usability Telemetry Client
 */

export interface TelemetryEvent {
  eventType: "page_view" | "interaction" | "error" | "web_vital" | "workspace_switch" | "app_launch";
  name: string;
  timestamp: string;
  url: string;
  data?: Record<string, unknown>;
  sessionDurationMs?: number;
}

class TelemetryClient {
  private queue: TelemetryEvent[] = [];
  private batchSize = 10;
  private flushIntervalMs = 5000;
  private isInitialized = false;
  private startTime = Date.now();

  public init() {
    if (typeof window === "undefined" || this.isInitialized) return;
    this.isInitialized = true;
    this.startTime = Date.now();

    // Listen for uncaught client-side errors
    window.addEventListener("error", (event) => {
      this.trackError("uncaught_error", event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        stack: event.error?.stack,
      });
    });

    window.addEventListener("unhandledrejection", (event) => {
      this.trackError("unhandled_rejection", String(event.reason), {
        reason: event.reason,
      });
    });

    // Flush remaining metrics on page unload
    window.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.flush(true);
      }
    });

    // Start auto-flush loop
    setInterval(() => this.flush(), this.flushIntervalMs);
  }

  public trackPageView(path: string, title?: string) {
    this.enqueue({
      eventType: "page_view",
      name: path,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      data: { title: title || document.title },
    });
  }

  public trackInteraction(action: string, target: string, meta?: Record<string, unknown>) {
    this.enqueue({
      eventType: "interaction",
      name: action,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      data: { target, ...meta },
    });
  }

  public trackAppLaunch(appId: string, appName?: string) {
    this.enqueue({
      eventType: "app_launch",
      name: appId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      data: { appName },
    });
  }

  public trackWorkspaceSwitch(workspaceId: string, workspaceName?: string) {
    this.enqueue({
      eventType: "workspace_switch",
      name: workspaceId,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      data: { workspaceName },
    });
  }

  public trackWebVital(name: string, value: number, rating?: string) {
    this.enqueue({
      eventType: "web_vital",
      name,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      data: { value, rating },
    });
  }

  public trackError(name: string, message: string, meta?: Record<string, unknown>) {
    this.enqueue({
      eventType: "error",
      name,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      data: { message, ...meta },
    });
  }

  private enqueue(event: TelemetryEvent) {
    event.sessionDurationMs = Date.now() - this.startTime;
    this.queue.push(event);
    if (this.queue.length >= this.batchSize) {
      this.flush();
    }
  }

  public async flush(useBeacon = false) {
    if (this.queue.length === 0) return;

    const eventsToSend = [...this.queue];
    this.queue = [];

    const payload = JSON.stringify({ events: eventsToSend });

    if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon("/api/v1/telemetry/events", payload);
      return;
    }

    try {
      await fetch("/api/v1/telemetry/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      });
    } catch {
      // Re-queue if fetch failed
      this.queue.unshift(...eventsToSend);
    }
  }
}

export const rum = new TelemetryClient();
